import * as React from 'react'
import classnames from 'classnames'
import {
    MouseEventHandler,
    PropsWithChildren,
    ReactNode,
    RefObject,
} from 'react'
import { Icon } from '@atoms'

export type LinkButtonProps = {
    name: string
    variant?: 'primary' | 'secondary'
    iconBefore?: string
    iconAfter?: string
    disabled?: boolean
    label: ReactNode
    onClick?: MouseEventHandler
    buttonRef?: RefObject<HTMLButtonElement>
} & PropsWithChildren

export const LinkButton: React.FC<LinkButtonProps> = ({
    name,
    variant = 'primary',
    iconBefore,
    iconAfter,
    label,
    disabled,
    onClick,
    buttonRef,
}) => {
    const baseStyles =
        'cursor-pointer text-sm leading-6 flex items-center gap-1 px-0.5 py-1 focus:outline-4 rounded-lg'

    const variants = {
        primary: {
            default: 'text-azure',
            focused: 'focus:text-blue-600 focus:outline-blue-200',
            hover: 'hover:text-blue-600',
            clicked: '[&:not(:disabled)]:active:text-blue-700',
            disabled: 'disabled:text-disabled',
        },
        secondary: {
            default: 'text-regular',
            focused: 'focus:text-regular focus:outline-blue-200',
            hover: 'hover:text-black',
            clicked: '[&:not(:disabled)]:active:text-black',
            disabled: 'disabled:bg-white disabled:text-disabled',
        },
    }

    const variantObj = variants[variant]
    const className = classnames(
        baseStyles,
        variantObj.default,
        variantObj.hover,
        variantObj.disabled,
        variantObj.clicked,
        variantObj.focused
    )

    return (
        <button
            disabled={disabled}
            onClick={onClick}
            className={className}
            data-testid={name}
            ref={buttonRef}
        >
            {iconBefore && <Icon name={iconBefore} size="small" />}
            <div className="grow">{label}</div>
            {iconAfter && <Icon name={iconAfter} size="small" />}
        </button>
    )
}
